/**
 * Sapphire cart mode (1.6.1): the checkout rules for a multi-product cart.
 * Everything here is for a config with gateway: 'sapphire' and is reached
 * only from the Sapphire path (doCompleteOrderSapphire, the 'sapphire' lead,
 * render() / the quick lead behind isSapphireMode()).
 */
import $ from 'jquery';
import { getConfig } from './config';
import { normalizePageName } from './routing';
import { getSapphireCart } from './sapphireCart';

// The checkout gateway's /lead and /order pass product1..product4 (with
// their _qty) to Konnektive and drop anything after that.
export const SAPPHIRE_MAX_LINES = 4;

// Where a Sapphire order lands when the config does not say: the root
// thank-you page Sapphire sites use (ordernutra.com/thankyou.html?orderID=).
export const SAPPHIRE_DEFAULT_THANK_YOU_PAGE = 'thankyou.html';

export function isSapphireMode() {
	return getConfig().gateway === 'sapphire';
}

// The campaign the gateway session is opened with (checkoutGateway.js
// campaignId()) and the lead is sent with (buildLeadParams).
function sessionCampaign() {
	const products = getConfig().products || [];
	return String((products[0] && products[0].campaignID_konnektive) || '');
}

function cartLines(lines) {
	if (Array.isArray(lines)) return lines;
	const cart = getSapphireCart();
	cart.loadCart();
	return cart.getCart().cart || [];
}

// A line saved without a quantity counts as 1 (1.6.0 sent 1 for every line).
// Anything else must be a whole number of 1 or more; null when it is not.
// (No Number.isInteger / Number.isNaN: babel-plugin-minify-builtins fails the
// production build on them, see showThankYouFallback in api.js.)
function lineQuantity(line) {
	const raw = line.itemQuantity;
	if (raw === undefined || raw === null || raw === '') return 1;
	const qty = Number(raw);
	return qty >= 1 && qty % 1 === 0 ? qty : null;
}

function lineName(line) {
	return line.itemName ? `"${line.itemName}"` : `Product ${line.itemID}`;
}

/**
 * Why this cart cannot be ordered, as { reason, message }, or null.
 * Checked in the browser so the shopper gets a clear message instead of a
 * gateway refusal (403 "Campaign mismatch") or lines dropped after the 4th.
 */
export function sapphireCartRefusal(lines) {
	const items = cartLines(lines);
	if (!items.length) {
		return {
			reason: 'empty-cart',
			message: 'Your cart is empty. Please add a product before you check out.',
		};
	}
	if (items.length > SAPPHIRE_MAX_LINES) {
		const extra = items.length - SAPPHIRE_MAX_LINES;
		return {
			reason: 'too-many-lines',
			message: `Your cart has ${items.length} different products. One order can hold up to `
				+ `${SAPPHIRE_MAX_LINES} different products. Please remove ${extra} and try again.`,
		};
	}
	const campaign = sessionCampaign();
	// A line saved with no campaign is let through: it can only be ordered
	// on the session's campaign anyway.
	const hasCampaign = (line) => line.itemCampaignID !== undefined
		&& line.itemCampaignID !== null && line.itemCampaignID !== '';
	const other = items.find((line) => campaign && hasCampaign(line)
		&& String(line.itemCampaignID) !== campaign);
	if (other) {
		return {
			reason: 'campaign-mismatch',
			message: `${lineName(other)} cannot be ordered together with the other products in this `
				+ 'checkout. Please remove it from your cart and try again.',
		};
	}
	const badQty = items.find((line) => lineQuantity(line) === null);
	if (badQty) {
		return {
			reason: 'bad-quantity',
			message: `Please enter a quantity of 1 or more for ${lineName(badQty)}.`,
		};
	}
	return null;
}

// Page-facing check: '' when the cart can be ordered, otherwise the message.
export function sapphireCartCheck(lines) {
	const refusal = sapphireCartRefusal(lines);
	return refusal ? refusal.message : '';
}

/**
 * The Konnektive product fields for the cart lines, in cart order:
 * { product1_id, product1_qty, product2_id, product2_qty, ... }. These are
 * the names the gateway's /lead and /order allowlists pass to Konnektive's
 * /leads/import and /order/import unchanged.
 */
export function sapphireProductParams(lines) {
	const params = {};
	cartLines(lines).forEach((line, i) => {
		params[`product${i + 1}_id`] = line.itemID;
		params[`product${i + 1}_qty`] = lineQuantity(line);
	});
	return params;
}

function thankYouPage() {
	return String(getConfig().sapphireThankYouPage || SAPPHIRE_DEFAULT_THANK_YOU_PAGE);
}

// thankyou.html?orderID=<id>[&<pass-through params>]
export function sapphireThankYouUrl(orderId) {
	const page = thankYouPage();
	let url = `${page}${page.indexOf('?') === -1 ? '?' : '&'}orderID=${orderId}`;
	const passThrough = $('input[name="urlparams"]').val();
	if (passThrough) {
		url += `&${passThrough}`;
	}
	return url;
}

// True on the configured thank-you page in Sapphire mode, so render() runs
// the thank-you step there (e.g. page-thank-you.php, which is not named
// 'thankyou'). Always false outside Sapphire mode.
export function isSapphireThankYouPage() {
	if (!isSapphireMode()) return false;
	const path = thankYouPage().split(/[?#]/)[0];
	return normalizePageName(path) === normalizePageName(window.location.pathname);
}
